/**
 * VerificationBanner — de leerkracht vertellen dat hij nog wacht.
 *
 * HET GAT DAT DIT DICHT
 *
 * Migratie 0020 houdt een niet-geverifieerde leerkracht bij élke leerling weg.
 * Dat werkt, en het werkt volledig stil: hij logde gewoon in, zag een lege
 * klas, en kreeg geen enkele uitleg. Een blokkade zonder melding leest niet als
 * een blokkade maar als een defecte app — en dan gaat er een supportmelding
 * heen over "mijn leerlingen zijn weg" terwijl er niets kapot is.
 *
 * WAAROM EEN BANNER EN GEEN BLOKKADESCHERM
 *
 * Hem buiten de deur houden zou meer voelen als "af", maar is verkeerd: een
 * wachtende leerkracht mag de app verkennen, materiaal bekijken en zich
 * inlezen. Alleen leerlinggegevens zijn dicht. Een portaal dat helemaal niet
 * opent, maakt van een wachttijd een afwijzing.
 *
 * WAAROM NIET WEG TE KLIKKEN
 *
 * Dit is geen mededeling maar de verklaring voor alles wat leeg blijft. Wie hem
 * wegklikt, zit een minuut later naar een lege klas te kijken zonder reden.
 * Hij verdwijnt vanzelf zodra de status verandert — dat is het enige moment
 * waarop hij niets meer toevoegt.
 *
 * DE TWEE TEKSTEN
 *
 * Zonder school is het wachten op ons, met school op de collega's. Dat verschil
 * bepaalt wat iemand nú kan doen, en dat is het enige dat een wachtscherm hoort
 * te vertellen. Zie `docs/specs/TEACHER_VERIFICATION.md` §A.3.
 */

import React from 'react';
import { Clock, ShieldX } from 'lucide-react';
interface VerificationBannerProps {
  status: 'pending' | 'rejected';
  /** De school; leeg betekent solo-leerkracht. */
  schoolName?: string;
  /**
   * Is die school uit het register GEKOZEN (0041), of zelf ingetypt?
   *
   * Bij een aanvraag wacht deze leerkracht op iets anders dan hij denkt: eerst
   * moeten wij zijn school opnemen, daarna wordt hij zelf beoordeeld. Zonder dat
   * onderscheid leest hij "we controleren of je daar werkt" terwijl de school
   * waar hij op wacht bij ons nog helemaal niet bestaat — en dan wacht hij op de
   * verkeerde partij.
   */
  schoolFromRegistry?: boolean;
}
export function VerificationBanner({
  status,
  schoolName,
  schoolFromRegistry = false
}: VerificationBannerProps) {
  if (status === 'rejected') {
    return <div role="status" className="border-b border-rose-200 bg-rose-50 px-4 py-3 md:px-6" data-id="element-11114" data-mp="1bneyqte30d1o">
        <div className="mx-auto flex max-w-5xl gap-3" data-id="element-11115" data-mp="1gdn2svkznbhl">
          <ShieldX className="mt-0.5 h-5 w-5 shrink-0 text-rose-600" aria-hidden="true" data-id="element-11116" data-mp="1gf6iol2qfkiu" />
          <div data-id="element-11117" data-mp="1lcgh2q83n6it">
            <p className="text-sm font-bold text-rose-900" data-id="element-11118" data-mp="1lcq4q75o91lc">
              Je aanvraag is niet goedgekeurd
            </p>
            <p className="mt-0.5 text-sm leading-relaxed text-rose-800" data-id="element-11119" data-mp="1vb5zql7tkv04">
              Je kunt de app blijven gebruiken om materiaal te bekijken, maar je
              krijgt geen toegang tot gegevens van leerlingen. Denk je dat dit
              niet klopt? Laat je school contact met ons opnemen.
            </p>
          </div>
        </div>
      </div>;
  }
  const hasSchool = Boolean(schoolName?.trim());
  return <div role="status" className="border-b border-amber-200 bg-amber-50 px-4 py-3 md:px-6" data-id="element-11120" data-mp="3tkbkr65ec1vw">
      <div className="mx-auto flex max-w-5xl gap-3" data-id="element-11121" data-mp="3otv319ujlu3q">
        <Clock className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" aria-hidden="true" data-id="element-11122" data-mp="3osbq9t83i16p" />
        <div data-id="element-11123" data-mp="3jvlal707d7lt">
          <p className="text-sm font-bold text-amber-900" data-id="element-11124" data-mp="3jvbfx647qc9s">
            Je account wacht op goedkeuring
          </p>
          <p className="mt-0.5 text-sm leading-relaxed text-amber-900/90" data-id="element-11125" data-mp="3fr3t5ehbf6e7">
            {hasSchool && schoolFromRegistry ? <>
                We controleren of je bij <strong data-id="element-11126" data-mp="bg1l8j2nk9c1">{schoolName}</strong> werkt.
                Werken er al collega's met Meester Djay? Dan gaat het sneller
                als een van hen je toevoegt.
              </> : hasSchool ? <>
                Je hebt <strong data-id="element-11127" data-mp="2dsb3ubaqhz6">{schoolName}</strong> aangevraagd — die school
                staat nog niet bij ons. We nemen eerst contact op met de school
                zelf; daarna kijken we naar jouw account. Collega's die al met
                Meester Djay werken kunnen je wél nu toevoegen.
              </> : <>
                Je hebt geen school opgegeven. Om bij gegevens van leerlingen te
                mogen, hoort er een school achter je te staan — of een ouder die
                je koppelt aan zijn kind.
              </>}
          </p>
          <p className="mt-1.5 text-sm leading-relaxed text-amber-900/90" data-id="element-11128" data-mp="9wq8i6v58h5k">
            Tot die tijd kun je alles bekijken, maar blijven je klassen leeg.
            Dat is geen storing.
          </p>
        </div>
      </div>
    </div>;
}